import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline pencil-icon quick edit for the church name.
 * Only rendered for main_admin users. Wraps `children` (the church-name
 * label) and pops open a small textbox to save via `PATCH /admin/branding`.
 * On success it refreshes AuthContext so the new name appears everywhere
 * (TopBar, Landing, Hero) without a hard reload.
 */
export default function EditableChurchName({ children, className = "" }) {
  const { user, church, refresh } = useAuth();
  const canEdit = user?.role === "main_admin";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(church?.name || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setValue(church?.name || "");
  }, [church?.name, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (!canEdit) return <>{children}</>;

  const save = async (e) => {
    e?.preventDefault?.();
    const trimmed = value.trim();
    if (!trimmed) { toast.error("Name required"); return; }
    if (trimmed === (church?.name || "").trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch("/admin/branding", { name: trimmed });
      await refresh();
      toast.success("Church name updated");
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className={`flex items-center gap-1 ${className}`} data-testid="edit-church-name-form">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          maxLength={80}
          className="min-w-0 flex-1 bg-transparent border-b border-stone-300 outline-none font-serif-display text-lg sm:text-xl focus:border-stone-500 py-0.5"
          data-testid="edit-church-name-input"
          onKeyDown={(e) => { if (e.key === "Escape") setEditing(false); }}
        />
        <button
          type="submit"
          disabled={saving}
          className="p-1 rounded-full text-emerald-600 hover:bg-emerald-50 shrink-0"
          data-testid="edit-church-name-save"
          aria-label="Save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          className="p-1 rounded-full text-stone-500 hover:bg-stone-100 shrink-0"
          aria-label="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </form>
    );
  }

  return (
    <div className={`flex items-center gap-1.5 min-w-0 ${className}`}>
      <div className="min-w-0 truncate">{children}</div>
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid="edit-church-name-open"
        aria-label="Edit church name"
        className="p-1 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 shrink-0 opacity-70 hover:opacity-100 transition"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
